import React, { useState, useEffect } from 'react';
import axios from 'axios';
import io from 'socket.io-client';
import { API_URL } from '../config';

function LiveBattle({ matchId, user, token, onClose }) {
  const [match, setMatch] = useState(null);
  const [battle, setBattle] = useState(null);
  const [battleEvents, setBattleEvents] = useState([]);
  const [timeRemaining, setTimeRemaining] = useState(600); // 10 minutes
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [socket, setSocket] = useState(null);
  const [battlePhase, setBattlePhase] = useState('preparation'); // preparation, active, finished

  useEffect(() => {
    if (matchId && token) {
      fetchMatch();
      setupWebSocket();
    }
    
    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, [matchId, token]);

  const setupWebSocket = () => {
    const newSocket = io(API_URL, {
      auth: { token }
    });

    newSocket.on('connect', () => {
      console.log('Connected to live battle socket');
      newSocket.emit('join_match', matchId);
    });

    newSocket.on('match_started', (data) => {
      if (data.matchId === matchId) {
        setBattlePhase('active');
        setBattleEvents(prev => [...prev, '🔥 La battaglia è iniziata!']);
      }
    });

    newSocket.on('battle_event', (data) => {
      if (data.matchId === matchId) {
        setBattleEvents(prev => [...prev, data.event]);
        setTimeRemaining(Math.floor(data.timeRemaining / 1000));
      }
    });

    newSocket.on('match_finished', (data) => {
      if (data.matchId === matchId) {
        setBattlePhase('finished');
        setBattleEvents(prev => [...prev, 
          data.winner ? 
            `🏆 ${data.winner} ha vinto la battaglia!` : 
            '🤝 La battaglia è terminata in pareggio!'
        ]);
        setTimeRemaining(0);
        
        // Fetch final battle results
        fetchBattleResults();
      }
    });

    setSocket(newSocket);
  };

  const fetchMatch = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/matches/${matchId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMatch(response.data);
      
      // Check battle status
      if (response.data.status === 'active') {
        setBattlePhase('active');
      } else if (response.data.status === 'ended') {
        setBattlePhase('finished');
        fetchBattleResults();
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Errore nel caricamento della partita');
    } finally {
      setLoading(false);
    }
  };

  const fetchBattleResults = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/matches/${matchId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.battles && response.data.battles.length > 0) {
        setBattle(response.data.battles[0]);
      }
      setMatch(response.data);
    } catch (error) {
      console.error('Error fetching battle results:', error);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const translateAttribute = (attribute) => {
    const translations = {
      'LIGHT': 'Luce', 'DARK': 'Tenebre', 'EARTH': 'Terra',
      'WATER': 'Acqua', 'FIRE': 'Fuoco', 'WIND': 'Vento', 'DIVINE': 'Divino'
    };
    return translations[attribute] || attribute;
  };

  const getCardPowerDisplay = (metadata) => {
    let power = 0;
    if (metadata.atk || metadata.attack) {
      power += parseInt(metadata.atk || metadata.attack) || 1000;
    }
    if (metadata.def || metadata.defense) {
      power += Math.floor((parseInt(metadata.def || metadata.defense) || 800) * 0.5);
    }
    if (metadata.level) {
      power += parseInt(metadata.level) * 100;
    }
    return power;
  };

  if (loading) {
    return (
      <div className="live-battle">
        <div className="loading-battle">
          <h3>⚔️ Caricamento battaglia in corso...</h3>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="live-battle">
        <div className="error-message">{error}</div>
        <button className="btn btn-secondary" onClick={onClose}>
          ← Torna indietro
        </button>
      </div>
    );
  }

  if (!match) {
    return (
      <div className="live-battle">
        <div className="error-message">Partita non trovata</div>
        <button className="btn btn-secondary" onClick={onClose}>
          ← Torna indietro
        </button>
      </div>
    );
  }

  return (
    <div className="live-battle">
      <div className="battle-header">
        <h2>⚔️ Battaglia Live</h2>
        <div className="match-info">
          <h3>{match.player1_name} VS {match.player2_name}</h3>
          <p>📅 {new Date(match.scheduled_time).toLocaleString('it-IT')}</p>
          <p>🏟️ Torneo: {match.tournament_name}</p>
        </div>
        
        {battlePhase === 'active' && (
          <div className="battle-timer">
            <h3>⏰ Tempo rimanente: {formatTime(timeRemaining)}</h3>
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${(timeRemaining / 600) * 100}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Team Presentation */}
      {battlePhase === 'preparation' && (
        <div className="team-presentation">
          <h3>🎭 Presentazione Squadre</h3>
          
          <div className="teams-showcase">
            <div className="team-section">
              <h4>🔥 {match.player1_name}</h4>
              <div className="team-cards">
                {match.player1_cards.map(card => {
                  const metadata = card.metadata ? 
                    (typeof card.metadata === 'string' ? JSON.parse(card.metadata) : card.metadata) : {};
                  
                  return (
                    <div key={card.id} className="team-card">
                      {card.image_url && (
                        <img 
                          src={`${API_URL}/cards/${card.image_url}`}
                          alt={card.name}
                          className="team-card-image"
                        />
                      )}
                      <div className="team-card-info">
                        <h5>{card.name}</h5>
                        <p>⚡ {getCardPowerDisplay(metadata)}</p>
                        {metadata.attribute && (
                          <p>🔮 {translateAttribute(metadata.attribute)}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="vs-showcase">
              <h2>VS</h2>
              <p>🎬 La battaglia inizierà automaticamente all'orario programmato</p>
            </div>

            <div className="team-section">
              <h4>⚡ {match.player2_name}</h4>
              <div className="team-cards">
                {match.player2_cards.map(card => {
                  const metadata = card.metadata ? 
                    (typeof card.metadata === 'string' ? JSON.parse(card.metadata) : card.metadata) : {};
                  
                  return (
                    <div key={card.id} className="team-card">
                      {card.image_url && (
                        <img 
                          src={`${API_URL}/cards/${card.image_url}`}
                          alt={card.name}
                          className="team-card-image"
                        />
                      )}
                      <div className="team-card-info">
                        <h5>{card.name}</h5>
                        <p>⚡ {getCardPowerDisplay(metadata)}</p>
                        {metadata.attribute && (
                          <p>🔮 {translateAttribute(metadata.attribute)}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Live Battle */}
      {battlePhase === 'active' && (
        <div className="live-battle-field">
          <h3>🔥 Battaglia in Corso!</h3>
          
          <div className="battlefield-animation">
            <div className="battlefield-bg">
              <div className="battle-effects"></div>
            </div>
          </div>

          <div className="battle-commentary">
            <h4>📺 Cronaca Live</h4>
            <div className="events-feed">
              {battleEvents.map((event, index) => (
                <div key={index} className="battle-event-live">
                  {event}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Battle Results */}
      {battlePhase === 'finished' && battle && (
        <div className="battle-results">
          <h3>🏆 Risultati Finali</h3>
          
          <div className="final-battle-cards">
            <div className="final-battle-card">
              {match.player1_cards.find(c => c.card_id === battle.card1_id)?.image_url && (
                <img 
                  src={`${API_URL}/cards/${match.player1_cards.find(c => c.card_id === battle.card1_id).image_url}`}
                  alt={battle.card1_name}
                  className="final-card-image"
                />
              )}
              <h4>{battle.card1_name}</h4>
              <p>💥 Danno: {battle.card1_damage_dealt}</p>
              {battle.winner_card_id === battle.card1_id && (
                <div className="winner-badge">🏆 VINCITORE</div>
              )}
            </div>
            
            <div className="final-vs">⚔️</div>
            
            <div className="final-battle-card">
              {match.player2_cards.find(c => c.card_id === battle.card2_id)?.image_url && (
                <img 
                  src={`${API_URL}/cards/${match.player2_cards.find(c => c.card_id === battle.card2_id).image_url}`}
                  alt={battle.card2_name}
                  className="final-card-image"
                />
              )}
              <h4>{battle.card2_name}</h4>
              <p>💥 Danno: {battle.card2_damage_dealt}</p>
              {battle.winner_card_id === battle.card2_id && (
                <div className="winner-badge">🏆 VINCITORE</div>
              )}
            </div>
          </div>

          <div className="match-result">
            {match.winner_id ? (
              <h2 className="match-winner">
                🎉 {match.winner_id === match.player1_id ? match.player1_name : match.player2_name} vince la partita!
              </h2>
            ) : (
              <h2 className="match-draw">🤝 Partita terminata in pareggio!</h2>
            )}
            
            <p>📊 Punteggio finale: {match.player1_score} - {match.player2_score}</p>
          </div>

          <div className="battle-commentary">
            <h4>📋 Cronaca Completa</h4>
            <div className="events-feed final-events">
              {battleEvents.map((event, index) => (
                <div key={index} className="battle-event-final">
                  {event}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="battle-controls">
        <button 
          className="btn btn-secondary" 
          onClick={onClose}
        >
          ← Torna ai Tornei
        </button>
      </div>
    </div>
  );
}

export default LiveBattle;